import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Seo } from "@/components/Seo";
import { PageHeader } from "@/components/common/PageHeader";
import { SectionHeading } from "@/components/common/SectionHeading";
import { Reveal } from "@/components/common/Reveal";
import { AlertCircle, CheckCircle2, ArrowRight, ShieldCheck } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";

const warningSigns = [
  "A mouth ulcer that does not heal within three weeks",
  "Red or white patches inside the mouth",
  "Unexplained lumps or thickening in the mouth or neck",
  "Persistent difficulty or pain when swallowing",
  "A sore throat or hoarseness that persists",
  "Numbness or unexplained bleeding in the mouth",
];

const steps = [
  { title: "Gentle conversation", text: "We discuss your history, habits, and any concerns — without judgement or fear tactics." },
  { title: "Careful examination", text: "A thorough, painless visual and physical screening of the mouth and surrounding tissues." },
  { title: "Clear guidance", text: "If anything needs attention, we explain it calmly and outline the next steps." },
  { title: "Timely action & support", text: "Where needed, we arrange appropriate investigation and a structured monitoring plan." },
];

export default function OralCancerCentre() {
  const [faqs, setFaqs] = useState([]);
  useEffect(() => { api.get("/faqs?category=Mouth Cancer").then((r) => setFaqs(r.data)).catch(() => {}); }, []);

  const jsonLd = { "@context": "https://schema.org", "@type": "MedicalClinic", name: "CDT Oral Cancer & Precancer Centre", medicalSpecialty: "Oncology", description: "Specialist-led oral cancer screening and precancer care in Greater Noida." };

  return (
    <>
      <Seo title="Oral Cancer & Precancer Centre" description="Specialist-led, sensitive oral cancer screening and precancer care. Early detection can be life-saving." path="/oral-cancer-centre" jsonLd={jsonLd} />
      <PageHeader eyebrow="A Dedicated Centre" title="Oral Cancer & Precancer Centre" subtitle="Led by specialist Dr Ritika Sharma, our centre provides calm, sensitive screening and education. Early detection saves lives — and we're here to help, without fear." image="https://images.unsplash.com/photo-1651008376811-b90baee60c1f?auto=format&fit=crop&w=1200&q=80" />

      <section className="py-14 lg:py-20">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal>
            <div className="rounded-[var(--cdt-radius-md)] bg-[rgba(200,168,107,0.1)] border border-[color:var(--cdt-border)] p-6 flex gap-4">
              <ShieldCheck className="h-6 w-6 text-[var(--cdt-primary)] flex-shrink-0 mt-0.5" />
              <p className="text-sm leading-7 text-[var(--cdt-text)]">Screening is quick, painless, and confidential. Whether you have a specific concern or simply want peace of mind, we welcome you with care and respect.</p>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="pb-4">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeading align="left" eyebrow="When To Seek Screening" title="Signs worth checking" subtitle="If you notice any of the following, please don't wait — early attention makes all the difference." />
          <div className="mt-8 grid sm:grid-cols-2 gap-3">
            {warningSigns.map((w, i) => (
              <Reveal key={w} delay={(i % 2) * 0.05}>
                <div className="flex items-start gap-3 rounded-[var(--cdt-radius-sm)] bg-white border border-[color:var(--cdt-border)] px-4 py-3">
                  <AlertCircle className="h-5 w-5 text-[var(--cdt-accent)] flex-shrink-0 mt-0.5" />
                  <span className="text-sm text-[var(--cdt-text)] leading-6">{w}</span>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="py-14 lg:py-20">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeading align="left" eyebrow="What To Expect" title="A calm, respectful screening" />
          <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {steps.map((s, i) => (
              <Reveal key={s.title} delay={i * 0.06}>
                <div className="h-full rounded-[var(--cdt-radius-md)] bg-[var(--cdt-surface-2)] border border-[color:var(--cdt-border)] p-5">
                  <div className="font-display text-2xl text-[var(--cdt-accent)]">{String(i + 1).padStart(2, "0")}</div>
                  <h3 className="font-semibold mt-2 text-[var(--cdt-text)] text-[15px]">{s.title}</h3>
                  <p className="mt-2 text-sm leading-6 text-[var(--cdt-muted)]">{s.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {faqs.length > 0 && (
        <section className="py-14 lg:py-20 bg-white">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
            <SectionHeading eyebrow="Common Questions" title="Oral cancer, answered" />
            <div className="mt-8">
              <Accordion type="single" collapsible>
                {faqs.map((f, i) => (
                  <AccordionItem key={i} value={`f-${i}`} className="border-[color:var(--cdt-border)]">
                    <AccordionTrigger className="text-left font-medium text-[var(--cdt-text)]">{f.question}</AccordionTrigger>
                    <AccordionContent className="text-[var(--cdt-muted)] leading-7">{f.answer}</AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>
          </div>
        </section>
      )}

      <section className="py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="rounded-[var(--cdt-radius-lg)] bg-[var(--cdt-primary)] p-10 text-center">
            <h2 className="font-display text-3xl text-white">Request a screening consultation</h2>
            <p className="mt-3 text-white/80 max-w-xl mx-auto leading-7">A short, gentle appointment for reassurance or early detection. We're glad you're taking this step.</p>
            <Link to="/appointment" data-testid="oral-cancer-appointment-button" className="mt-7 inline-flex items-center gap-2 h-12 px-6 rounded-[var(--cdt-radius-sm)] bg-white text-[var(--cdt-primary-2)] font-semibold hover:bg-[var(--cdt-surface-2)] transition-colors">Request Screening <ArrowRight className="h-4 w-4" /></Link>
          </div>
        </div>
      </section>
    </>
  );
}
